import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Autocomplete,
  Box,
  Grid,
  Paper,
  Chip,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  fetchPlans,
  fetchNetworks,
  fetchCompanies,
  createPlan,
  updatePlan,
  deletePlan,
  searchBenefits,
  fetchPlan,
  fetchInsuranceCategories,
  fetchEstablishmentsSelect,
  bulkUploadPlans,
} from "../../../apis/services/insuranceApi";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";

import ConfirmDialog from "../../../shared/components/ConfirmDialog";
import CustomIconButton from "../../../shared/components/CustomIconButton";

const categoryOrder = ["inpatient", "outpatient", "optical", "dental"];
const categoryLabels = {
  inpatient: "Inpatient",
  outpatient: "Outpatient",
  optical: "Optical",
  dental: "Dental",
};

const PlansTab = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [id, setId] = useState(null);
  const [planName, setPlanName] = useState("");
  const [annualLimit, setAnnualLimit] = useState("");
  const [areaOfCover, setAreaOfCover] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [strikePrice, setStrikePrice] = useState("");
  const [coverAmount, setCoverAmount] = useState("");
  const [features, setFeatures] = useState([]);
  const [discountText, setDiscountText] = useState("");
  const [specialForCustomers, setSpecialForCustomers] = useState(false);
  const [recommended, setRecommended] = useState(false);
  const [isDhaApproved, setIsDhaApproved] = useState(false);
  const [eligibility, setEligibility] = useState({
    age_min: "",
    age_max: "",
    residency: ""
  });
  const [policyTermYears, setPolicyTermYears] = useState(1);
  const [isRenewable, setIsRenewable] = useState(true);

  const fileInputRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishments, setSelectedEstablishments] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [insuranceCategories, setInsuranceCategories] = useState([]);
  const [selectedSpecialities, setSelectedSpecialities] = useState([]);

  const [categories, setCategories] = useState({
    inpatient: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
    outpatient: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
    optical: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
    dental: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
  });

  const [claimSteps, setClaimSteps] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [highlights, setHighlights] = useState([]);

  const [benefitOptions, setBenefitOptions] = useState({});
  const [benefitInput, setBenefitInput] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const columns = [
    { field: "name", headerName: "Plan Name", flex: 1 },
    { field: "sub_title", headerName: "Sub Title", flex: 1 },
    {
      field: "company",
      headerName: "Company",
      flex: 1,
      valueGetter: (params) => params.row.network?.company?.name || "-",
    },
    {
      field: "network",
      headerName: "Network",
      flex: 1,
      valueGetter: (params) => params.row.network?.name || "-",
    },
    { field: "annual_limit", headerName: "Annual Limit", flex: 1 },
    { field: "selling_price", headerName: "Selling Price", flex: 1 },
    { field: "cover_amount", headerName: "Cover Amount", flex: 1 },
    { field: "area_of_cover", headerName: "Area of Cover", flex: 1 },
    {
      field: "recommended",
      headerName: "Recommended",
      flex: 0.5,
      renderCell: (params) => (
        params.value ? <Chip label="Yes" color="success" size="small" /> : <Chip label="No" color="default" size="small" />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      getActions: (params) => [
        <CustomIconButton
          key="view"
          onClickAction={() => navigate(`/plan/${params.id}`)}
          arialLabel="view"
          icon={<VisibilityIcon />}
        />,
        <CustomIconButton
          key="edit"
          onClickAction={() => handleEdit(params.id)}
          arialLabel="edit"
          icon={<EditIcon />}
        />,
        <CustomIconButton
          key="delete"
          onClickAction={() => handleDeleteClick(params.id)}
          arialLabel="delete"
          icon={<DeleteIcon />}
        />,
      ],
    },
  ];

  const loadPlans = useCallback(
    async (pageNo = page, limit = pageSize) => {
      setLoading(true);
      try {
        const res = await fetchPlans(pageNo, limit, searchText);

        if (res.data.success) {
          const mapped = res.data.data.rows.map((item) => ({
            id: item.id,
            name: item.name,
            sub_title: item.sub_title || "",
            annual_limit: item.annual_limit,
            selling_price: item.selling_price || "",
            cover_amount: item.cover_amount || "",
            area_of_cover: item.area_of_cover,
            recommended: item.recommended || false,
            network: item.network,
          }));

          setRows(mapped);
          setTotalRows(res.data.data.count);
        }
      } catch (err) {
        toast.error("Failed to load plans");
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, searchText]
  );

  useEffect(() => {
    loadPlans(page, pageSize);
  }, [page, pageSize, searchText, loadPlans]);

  const loadCompanies = async () => {
    try {
      const res = await fetchCompanies();
      if (res.data.success) {
        setCompanies(res.data.data.rows || res.data.data);
      }
    } catch (err) {
      toast.error("Failed to load companies");
    }
  };

  const loadNetworks = async (companyId) => {
    try {
      const res = await fetchNetworks();
      if (res.data.success) {
        const filtered = res.data.data.rows.filter((n) => n.company_id === Number(companyId));
        setNetworks(filtered);
      }
    } catch (err) {
      toast.error("Failed to load networks");
    }
  };

  useEffect(() => {
    loadPlans();
    loadCompanies();

    fetchEstablishmentsSelect().then((res) => {
      if (!isMounted.current) return;
      if (res.data.success) setEstablishments(res.data.data || []);
    });

    fetchInsuranceCategories().then((res) => {
      if (!isMounted.current) return;
      if (res.data.success) {
        const list = Array.isArray(res.data.data)
          ? res.data.data
          : res.data.data?.rows || [];
        setInsuranceCategories(list);
      } else {
        setInsuranceCategories([]);
      }
    });
  }, [loadPlans]);

  useEffect(() => {
    if (selectedCompany) {
      loadNetworks(selectedCompany);
      setSelectedNetwork("");
    }
  }, [selectedCompany]);

  // ===================================================
  // ✅ NEW: Parse eligibility JSON array → 3 inputs
  // ===================================================
  const parseEligibilityFromArray = (arr = []) => {
    let age_min = "";
    let age_max = "";
    let residency = "";

    if (!Array.isArray(arr)) {
      return { age_min: "", age_max: "", residency: "" };
    }

    arr.forEach((item) => {
      if (typeof item !== "string") return;

      // Example: "Age between 18 and 65"
      if (item.toLowerCase().includes("age")) {
        const match = item.match(/(\d+)\D+(\d+)/);
        if (match) {
          age_min = match[1];
          age_max = match[2];
        }
      }

      // Example: "Residency: UAE"
      if (item.toLowerCase().includes("residency")) {
        residency = item.split(":")[1]?.trim() || "";
      }
    });

    return { age_min, age_max, residency };
  };

  const handleEdit = async (planId) => {
    try {
      setLoading(true);

      const res = await fetchPlan(planId);
      if (!res.data.success) {
        toast.error("Failed to load plan details");
        return;
      }

      const plan = res.data.data;

      // Basic details
      setId(planId);
      setPlanName(plan.name || "");
      setSubTitle(plan.sub_title || "");
      setDescription(plan.description || "");
      setSellingPrice(plan.selling_price || "");
      setStrikePrice(plan.strike_price || "");
      setCoverAmount(plan.cover_amount || "");
      setFeatures(Array.isArray(plan.features) ? plan.features : []);
      setDiscountText(plan.discount_text || "");
      setSpecialForCustomers(plan.special_for_customers || false);
      setRecommended(plan.recommended || false);
      setAnnualLimit(plan.annual_limit || "");
      setAreaOfCover(plan.area_of_cover || "");
      setIsDhaApproved(plan.is_dha_approved || false);

      // Eligibility - set as object
      setEligibility(parseEligibilityFromArray(plan.eligibility));

      setPolicyTermYears(plan.policy_term_years || 1);
      setIsRenewable(plan.is_renewable !== false);

      // Company + Network
      setSelectedCompany(plan.network?.company?.id || "");
      await loadNetworks(plan.network?.company?.id);
      setSelectedNetwork(plan.network?.id || "");

      // Multi Establishments
      setSelectedEstablishments(plan.establishments?.map(e => e.id) || []);

      // Specialities
      setSelectedSpecialities(
        Array.isArray(plan.specialities)
          ? plan.specialities.map(s => s.id)
          : []
      );

      // Categories & Benefits
      const loadedCategories = {};
      categoryOrder.forEach((catKey) => {
        const cat = plan.categories?.[catKey];

        loadedCategories[catKey] = cat
          ? {
            description: cat.description || "",
            co_payment: cat.co_payment || false,
            co_payment_info: cat.co_payment_info || "",
            benefits:
              cat.benefits?.map((b) => ({
                id: b.id,
                name: b.name || "",
                included: b.included !== false,
                notes: b.notes || "",
              })) || [],
          }
          : {
            description: "",
            co_payment: false,
            co_payment_info: "",
            benefits: [],
          };
      });

      setCategories(loadedCategories);

      // Additional sections
      setClaimSteps(
        plan.claimSteps?.map((s) => ({
          step_no: s.step_no,
          description: s.description
        })) || []
      );

      setExclusions(
        plan.exclusions?.map((e) => ({
          exclusion_text: e.exclusion_text
        })) || []
      );

      setHighlights(
        plan.highlights?.map((h) => ({
          title: h.title,
          // icon: h.icon || "",
          // action_type: h.action_type || null,
          order_no: h.order_no || 0
        })) || []
      );

      setOpen(true);
    } catch (err) {
      console.error("❌ Error loading plan", err);
      toast.error("Failed to load plan data");
    } finally {
      setLoading(false);
    }
  };

  const getRowsPerPageOptions = (count) => {
    if (count <= 10) return [10];
    if (count <= 20) return [10, 20];
    if (count <= 50) return [10, 25, 50];
    return [10, 25, 50, 100];
  };

  const handleDeleteClick = (planId) => {
    setDeletingId(planId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deletePlan(deletingId);
      toast.success("Plan deleted successfully");
      loadPlans();
    } catch (err) {
      toast.error("Failed to delete plan");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleBulkUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleBulkFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setBulkUploading(true);
      await bulkUploadPlans(file);
      toast.success("Plans uploaded successfully");
      loadPlans();
    } catch (err) {
      console.error("Bulk upload failed", err);
      toast.error(err.response?.data?.message || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
      e.target.value = "";
    }
  };

  const handleClose = () => {
    setOpen(false);
    setId(null);
    setPlanName("");
    setSubTitle("");
    setDescription("");
    setSellingPrice("");
    setStrikePrice("");
    setCoverAmount("");
    setFeatures([]);
    setDiscountText("");
    setSpecialForCustomers(false);
    setRecommended(false);
    setAnnualLimit("");
    setAreaOfCover("");
    setSelectedCompany("");
    setSelectedNetwork("");
    setSelectedEstablishments([]);
    setSelectedSpecialities([]);
    setCategories({
      inpatient: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
      outpatient: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
      optical: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
      dental: { description: "", co_payment: false, co_payment_info: "", benefits: [] },
    });
    setBenefitOptions({});
    setBenefitInput({});
    setIsDhaApproved(false);
    setEligibility({
      age_min: "",
      age_max: "",
      residency: ""
    });
    setPolicyTermYears(1);
    setIsRenewable(true);
    setClaimSteps([]);
    setExclusions([]);
    setHighlights([]);
  };

  const searchBenefitSuggestions = async (categoryKey, query) => {
    if (!query.trim()) {
      setBenefitOptions((prev) => ({ ...prev, [categoryKey]: [] }));
      return;
    }
    try {
      const res = await searchBenefits(query);
      if (res.data.success) {
        setBenefitOptions((prev) => ({ ...prev, [categoryKey]: res.data.data || [] }));
      }
    } catch (err) {
      console.error("Benefit search failed", err);
    }
  };

  const addBenefit = (categoryKey) => {
    setCategories((prev) => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        benefits: [...prev[categoryKey].benefits, { name: "", included: true, notes: "" }],
      },
    }));
  };

  const updateBenefit = (categoryKey, index, field, value) => {
    setCategories((prev) => {
      const newBenefits = [...prev[categoryKey].benefits];
      newBenefits[index] = { ...newBenefits[index], [field]: value };
      return { ...prev, [categoryKey]: { ...prev[categoryKey], benefits: newBenefits } };
    });
  };

  const removeBenefit = (categoryKey, index) => {
    setCategories((prev) => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        benefits: prev[categoryKey].benefits.filter((_, i) => i !== index),
      },
    }));
  };

  const addClaimStep = () => {
    setClaimSteps((prev) => [...prev, { step_no: prev.length + 1, description: "" }]);
  };

  const updateClaimStep = (index, field, value) => {
    setClaimSteps((prev) => {
      const newArr = [...prev];
      newArr[index][field] = field === "step_no" ? Number(value) : value;
      return newArr;
    });
  };

  const removeClaimStep = (index) => {
    setClaimSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const addExclusion = () => {
    setExclusions((prev) => [...prev, { exclusion_text: "" }]);
  };

  const updateExclusion = (index, value) => {
    setExclusions((prev) => {
      const newArr = [...prev];
      newArr[index].exclusion_text = value;
      return newArr;
    });
  };

  const removeExclusion = (index) => {
    setExclusions((prev) => prev.filter((_, i) => i !== index));
  };

  const addHighlight = () => {
    setHighlights((prev) => [...prev, { title: "", order_no: prev.length }]);
  };

  const updateHighlight = (index, field, value) => {
    setHighlights((prev) => {
      const newArr = [...prev];
      newArr[index][field] = field === "order_no" ? Number(value) : value;
      return newArr;
    });
  };

  const removeHighlight = (index) => {
    setHighlights((prev) => prev.filter((_, i) => i !== index));
  };


  const handleSubmit = async () => {
    if (!planName.trim()) return toast.error("Plan name is required");
    if (!selectedNetwork) return toast.error("Please select a network");

    // ------------------------------------------------
    // ✅ CHANGED: Build eligibility JSON array
    // ------------------------------------------------
    const eligibilityArray = [];

    if (eligibility.age_min || eligibility.age_max) {
      eligibilityArray.push(
        `Age between ${eligibility.age_min || "N/A"} and ${eligibility.age_max || "N/A"}`
      );
    }

    if (eligibility.residency) {
      eligibilityArray.push(`Residency: ${eligibility.residency}`);
    }

    const payload = {
      network_id: Number(selectedNetwork),
      name: planName.trim(),
      sub_title: subTitle,
      description,
      selling_price: sellingPrice ? Number(sellingPrice) : null,
      strike_price: strikePrice ? Number(strikePrice) : null,
      cover_amount: coverAmount ? Number(coverAmount) : null,
      features,
      discount_text: discountText,
      special_for_customers: specialForCustomers,
      recommended,
      annual_limit: annualLimit,
      area_of_cover: areaOfCover,
      is_dha_approved: isDhaApproved,

      // ✅ FIXED FIELD
      eligibility: eligibilityArray,

      policy_term_years: Number(policyTermYears),
      is_renewable: isRenewable,
      establishments: selectedEstablishments,
      specialities: selectedSpecialities,
      categories: {},
      claimSteps,
      exclusions,
      highlights,
    };

    // -----------------------------
    // BUILD CATEGORIES
    // -----------------------------
    categoryOrder.forEach((cat) => {
      const data = categories[cat];


      if (data.benefits.length > 0 || data.description || data.co_payment) {
        payload.categories[cat] = {
          description: data.description,
          co_payment: data.co_payment,
          co_payment_info: data.co_payment_info,
          benefits: data.benefits.map((b) => {
            const existing = benefitOptions[cat]?.find((opt) => opt.name === b.name);

            if (existing && existing.id) {
              return {
                benefit_id: existing.id,
                included: b.included,
                notes: b.notes,
              };
            }
            return {
              name: b.name,
              included: b.included,
              notes: b.notes,
            };
          }),
        };
      }
    });

    try {
      if (id) {
        await updatePlan(id, payload);
        toast.success("Plan updated successfully");
      } else {
        await createPlan(payload);
        toast.success("Plan created successfully");
      }

      handleClose();
      loadPlans();
    } catch (err) {
      console.error("❌ Failed to save plan:", err);
      toast.error(err.response?.data?.message || "Failed to save plan");
    }
  };


  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <TextField
          size="small"
          placeholder="Search Plans..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          sx={{ mb: 2, width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchText("");
                    setPage(1);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <div>
          <input
            type="file"
            accept=".csv,.xlsx"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleBulkFileChange}
          />
          <Button
            style={{ marginRight: "10px" }}
            variant="outlined"
            onClick={handleBulkUploadClick}
            disabled={bulkUploading}
          >
            {bulkUploading ? "Uploading..." : "Bulk Upload"}
          </Button>

          <Button variant="outlined" onClick={() => setOpen(true)}>
            Add Plan
          </Button>
        </div>
      </div>

      <div style={{ height: "75vh", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={totalRows}
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowsPerPageOptions={getRowsPerPageOptions(totalRows)}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newSize) => {
            const safeSize = Math.min(newSize, totalRows || newSize);
            setPageSize(safeSize);
            setPage(1);
          }}
          checkboxSelection
          density="compact"
          autoHeight
        />
      </div>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {id ? "Edit Plan" : "Add Plan"}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ background: "#fafafa" }}>
          {/* Top Section - Company & Plan Info */}
          <Box sx={{ p: 2, mb: 4, background: "white", borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Insurance Company *</InputLabel>
                  <Select
                    value={selectedCompany}
                    label="Insurance Company *"
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Network *</InputLabel>
                  <Select
                    value={selectedNetwork}
                    label="Network *"
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                  >
                    {networks.map((n) => (
                      <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Plan Name *"
                  fullWidth
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Sub Title"
                  fullWidth
                  value={subTitle}
                  onChange={(e) => setSubTitle(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Selling Price (AED)"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Strike Price (AED)"
                  fullWidth
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={strikePrice}
                  onChange={(e) => setStrikePrice(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Cover Amount (AED)"
                  fullWidth
                  type="number"
                  value={coverAmount}
                  onChange={(e) => setCoverAmount(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={features}
                  onChange={(e, value) => {
                    setFeatures(value.map(v => String(v)));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        key={index}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Features"
                      placeholder="Type feature and press Enter"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Discount Text"
                  fullWidth
                  value={discountText}
                  onChange={(e) => setDiscountText(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={establishments}
                  getOptionLabel={(opt) => opt?.name || ""}
                  value={establishments.filter(e =>
                    selectedEstablishments.includes(e.id)
                  )}
                  onChange={(e, value) =>
                    setSelectedEstablishments(value.map(v => v.id))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Establishments"
                      placeholder="Choose establishments"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Specialities</InputLabel>
                  <Select
                    multiple
                    value={selectedSpecialities}
                    label="Specialities"
                    onChange={(e) => setSelectedSpecialities(e.target.value)}
                    renderValue={(selected) =>
                      insuranceCategories
                        .filter(cat => selected.includes(cat.id))
                        .map(cat => cat.name)
                        .join(", ")
                    }
                  >
                    {insuranceCategories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        <Checkbox checked={selectedSpecialities.includes(cat.id)} />
                        <Typography>{cat.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Annual Limit"
                  fullWidth
                  value={annualLimit}
                  onChange={(e) => setAnnualLimit(e.target.value)}
                  placeholder="e.g., AED 1,000,000"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Area of Cover"
                  fullWidth
                  value={areaOfCover}
                  onChange={(e) => setAreaOfCover(e.target.value)}
                  placeholder="e.g., Worldwide"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Policy Term (Years)"
                  fullWidth
                  type="number"
                  value={policyTermYears}
                  onChange={(e) => setPolicyTermYears(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={specialForCustomers}
                      onChange={(e) => setSpecialForCustomers(e.target.checked)}
                    />
                  }
                  label="Special for Customers"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={recommended}
                      onChange={(e) => setRecommended(e.target.checked)}
                    />
                  }
                  label="Recommended"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDhaApproved}
                      onChange={(e) => setIsDhaApproved(e.target.checked)}
                    />
                  }
                  label="DHA Approved"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isRenewable}
                      onChange={(e) => setIsRenewable(e.target.checked)}
                    />
                  }
                  label="Renewable"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Minimum Age"
                  fullWidth
                  type="number"
                  value={eligibility.age_min}
                  onChange={(e) => setEligibility({
                    ...eligibility,
                    age_min: e.target.value
                  })}
                  placeholder="e.g., 18"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Maximum Age"
                  fullWidth
                  type="number"
                  value={eligibility.age_max}
                  onChange={(e) => setEligibility({
                    ...eligibility,
                    age_max: e.target.value
                  })}
                  placeholder="e.g., 65"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Residency"
                  fullWidth
                  value={eligibility.residency}
                  onChange={(e) => setEligibility({
                    ...eligibility,
                    residency: e.target.value
                  })}
                  placeholder="e.g., UAE"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Category Sections */}
          {categoryOrder.map((catKey) => (
            <Box
              key={catKey}
              sx={{ p: 3, mb: 3, background: "white", borderRadius: 2 }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                {categoryLabels[catKey]}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={categories[catKey].description}
                    onChange={(e) =>
                      setCategories((prev) => ({
                        ...prev,
                        [catKey]: { ...prev[catKey], description: e.target.value },
                      }))
                    }
                    placeholder="e.g., Full hospital stay coverage"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Co-payment Info"
                    value={categories[catKey].co_payment_info}
                    onChange={(e) =>
                      setCategories((prev) => ({
                        ...prev,
                        [catKey]: { ...prev[catKey], co_payment_info: e.target.value },
                      }))
                    }
                    placeholder="e.g., 10% co-payment up to Max AED 500"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={categories[catKey].co_payment}
                        onChange={(e) =>
                          setCategories((prev) => ({
                            ...prev,
                            [catKey]: { ...prev[catKey], co_payment: e.target.checked },
                          }))
                        }
                      />
                    }
                    label="Co-payment Applicable"
                  />
                </Grid>

                {/* Benefits Table */}
                <Grid item xs={12}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Benefit</strong></TableCell>
                        <TableCell width={120}><strong>Covered</strong></TableCell>
                        <TableCell><strong>Notes</strong></TableCell>
                        <TableCell width={50}></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {categories[catKey].benefits.map((benefit, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Autocomplete
                              freeSolo
                              options={benefitOptions[catKey] || []}
                              getOptionLabel={(option) =>
                                typeof option === "string" ? option : option.name || ""
                              }
                              inputValue={benefitInput[`${catKey}-${idx}`] || benefit.name}
                              onInputChange={(e, value) => {
                                setBenefitInput((prev) => ({
                                  ...prev,
                                  [`${catKey}-${idx}`]: value
                                }));
                                searchBenefitSuggestions(catKey, value);
                                updateBenefit(catKey, idx, "name", value);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="Benefit name"
                                />
                              )}
                            />
                          </TableCell>

                          <TableCell>
                            <Select
                              value={benefit.included ? "Covered" : "Not Covered"}
                              size="small"
                              onChange={(e) =>
                                updateBenefit(
                                  catKey,
                                  idx,
                                  "included",
                                  e.target.value === "Covered"
                                )
                              }
                            >
                              <MenuItem value="Covered">Covered</MenuItem>
                              <MenuItem value="Not Covered">Not Covered</MenuItem>
                            </Select>
                          </TableCell>

                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="e.g., Up to AED 1000/day"
                              value={benefit.notes}
                              onChange={(e) =>
                                updateBenefit(catKey, idx, "notes", e.target.value)
                              }
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => removeBenefit(catKey, idx)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addBenefit(catKey)}
                    sx={{ mt: 1 }}
                  >
                    Add Benefit
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}

          {/* Claim Steps Section */}
          <Box sx={{ p: 3, mb: 3, background: "white", borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Claim Steps
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={100}><strong>Step No</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claimSteps.map((step, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={step.step_no}
                        onChange={(e) => updateClaimStep(idx, "step_no", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={step.description}
                        onChange={(e) => updateClaimStep(idx, "description", e.target.value)}
                        placeholder="e.g., Download app"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeClaimStep(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} onClick={addClaimStep} sx={{ mt: 1 }}>
              Add Claim Step
            </Button>
          </Box>

          {/* Exclusions Section */}
          <Box sx={{ p: 3, mb: 3, background: "white", borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Exclusions
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Exclusion Text</strong></TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exclusions.map((ex, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        value={ex.exclusion_text}
                        onChange={(e) => updateExclusion(idx, e.target.value)}
                        placeholder="e.g., Cosmetic surgery"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeExclusion(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} onClick={addExclusion} sx={{ mt: 1 }}>
              Add Exclusion
            </Button>
          </Box>

          {/* Highlights Section */}
          <Box sx={{ p: 3, mb: 3, background: "white", borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Highlights
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Title</strong></TableCell>
                  {/* <TableCell width={150}><strong>Action Type</strong></TableCell> */}
                  <TableCell width={100}><strong>Order No</strong></TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highlights.map((hl, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={hl.title}
                        onChange={(e) => updateHighlight(idx, "title", e.target.value)}
                        placeholder="e.g., Instant Approval"
                      />
                    </TableCell>
                    {/* <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={hl.icon}
                        onChange={(e) => updateHighlight(idx, "icon", e.target.value)}
                        placeholder="e.g., speed_icon"
                      />
                    </TableCell> */}
                    {/* <TableCell>
                      <Select
                        fullWidth
                        size="small"
                        value={hl.action_type || ""}
                        onChange={(e) =>
                          updateHighlight(idx, "action_type", e.target.value || null)
                        }
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="info">Info</MenuItem>
                        <MenuItem value="view_list">View List</MenuItem>
                        <MenuItem value="view_details">View Details</MenuItem>
                      </Select>
                    </TableCell> */}
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={hl.order_no}
                        onChange={(e) => updateHighlight(idx, "order_no", e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeHighlight(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button startIcon={<AddIcon />} onClick={addHighlight} sx={{ mt: 1 }}>
              Add Highlight
            </Button>
          </Box>

        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        openDialog={deleteDialogOpen}
        title="Delete Plan"
        message="Are you sure you want to delete this insurance plan?"
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        handleDialogClose={() => setDeleteDialogOpen(false)}
        handleDialogAction={confirmDelete}
      />
    </>
  );
};

export default PlansTab;